import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useAuthStore } from './stores/useAuthStore';
import { useNotesStore } from './stores/useNotesStore';
import { useMusicStore } from './stores/useMusicStore';
import Header from './components/Header';
import LeftPanel from './components/LeftPanel';
import ControlPanel from './components/ControlPanel';
import ScrollArea from './components/ui/ScrollArea';
import ToastContainer from './components/ui/Toast';
import GoogleAnalytics from './components/GoogleAnalytics';
import { initializeCapacitorListeners } from './lib/capacitor-listeners';
import { shouldBypassAuth } from './lib/platform';
import type { Session as AuthSession } from '@supabase/supabase-js';

// Route components
import Home from './routes/Home';
import Pattern from './routes/Pattern';
import ControlChanges from './routes/ControlChanges';
import PrivacyPolicy from './routes/PrivacyPolicy';
import Test from './routes/Test';
import Reset from './routes/Reset';
import WasmBenchmark from './routes/WasmBenchmark';
import Share from './routes/Share';
import NotFound from './routes/NotFound';

import './App.css';

function AppLayout() {
  const [session, setSession] = useState<AuthSession | null>(null);

  useEffect(() => {
    // Initialize Capacitor listeners on mount (iOS/Android)
    initializeCapacitorListeners();

    // Bypass auth on iOS simulator for development
    if (shouldBypassAuth()) {
      console.log('🔓 iOS Simulator detected - bypassing authentication');
      setSession({ user: { id: 'simulator-user' } } as AuthSession);
    }
  }, []);

  useEffect(() => {
    // Skip auth state management if bypassing
    if (shouldBypassAuth()) return;

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <div className="flex flex-col h-full" data-testid="app-loaded">
      <Header session={session} />
      <main className="flex-1 flex overflow-hidden">
        <LeftPanel />
        <ScrollArea className="flex-1">
          <React.Suspense fallback={<div>Loading...</div>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/pattern/:deviceIndex" element={<Pattern />} />
              <Route path="/control-changes/:deviceIndex" element={<ControlChanges />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/test" element={<Test />} />
              <Route path="/reset" element={<Reset />} />
              <Route path="/wasm-benchmark" element={<WasmBenchmark />} />
              <Route path="/share/:encodedPreset" element={<Share />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </React.Suspense>
        </ScrollArea>
      </main>
      <ControlPanel />
      <ToastContainer />
    </div>
  );
}

// Create callbacks outside component (SolidJS-like - stable references)
// Using getState() ensures they always read fresh values (like SolidJS signals)
const createPlaybackCallbacks = () => ({
  // NOTE: shouldPlaySynth is handled per-device in useMusicStore.ts (line 638-643)
  // Do NOT override it here - each device needs its own check

  onNotePlayed: (note: string, partIndex: number, _noteIndexInPart: number) => {
    const musicStore = useMusicStore.getState();
    const device = musicStore.devices[0]; // Always use device 0 (first virtual synth) for global UI state
    const devicePattern = device?.pattern;

    if (!devicePattern) return;

    useMusicStore.setState({
      currentNote: note,
      currentInfo: `Part ${devicePattern.pattern.sequence[partIndex]}`,
      noteTrigger: musicStore.noteTrigger + 1,
      currentPlaybackPosition: partIndex, // Update global playback position
    });

    // NOTE: Per-device pattern positions are now updated in useMusicStore.ts device callbacks
    // MIDI output is also handled by per-device callbacks in useMusicStore.ts
  },

  onRest: (partIndex: number, _noteIndexInPart: number) => {
    const musicStore = useMusicStore.getState();
    const device = musicStore.devices[0]; // Always use device 0 (first virtual synth) for global UI state
    const devicePattern = device?.pattern;

    if (!devicePattern) return;

    useMusicStore.setState({
      currentNote: null,
      currentInfo: `Part ${devicePattern.pattern.sequence[partIndex]} (rest)`,
      currentPlaybackPosition: partIndex, // Update global playback position
    });

    // NOTE: Per-device pattern positions are now updated in useMusicStore.ts device callbacks
  },

  onPartChange: (_partName: string, partIndex: number) => {
    // Update global playback position
    useMusicStore.setState({ currentPlaybackPosition: partIndex });

    // NOTE: Per-device pattern positions are now updated in useMusicStore.ts device callbacks
  },
});

function AppProviders() {
  // Initialize stores once on mount (SolidJS onMount equivalent)
  useEffect(() => {
    useAuthStore.getState().initialize();
    // Pattern initialization now happens in useMusicStore (per-device)

    if (typeof window !== 'undefined') {
      useMusicStore.getState().initWebMidi();

      // Expose store to window for E2E testing
      (window as any).__MUSIC_STORE__ = useMusicStore;
    }
  }, []);

  // Guard to prevent duplicate startPlayback calls (must be outside useEffect to survive React Strict Mode remounts)
  const isRestartingRef = useRef(false);
  // Track the timeout ID so we can clear pending resets if a new restart is triggered
  const resetTimeoutRef = useRef<number | null>(null);

  // SolidJS-like reactive effect: subscribe to state changes
  // This is the closest equivalent to SolidJS's createEffect with automatic tracking
  useEffect(() => {
    // Track previous values to detect changes (like SolidJS)
    // Only track fields that should trigger playback restart
    const musicStore = useMusicStore.getState();
    const device = musicStore.devices[0]; // Use device 0 (first virtual synth) for config (multi-device playback handles all enabled devices)
    const devicePattern = device?.pattern;

    // Track enabled states of all devices
    const initialEnabledStates = musicStore.devices.map((d) => d.enabled).join(",");

    let previousValues = {
      start: useNotesStore.getState().start,
      bpm: useNotesStore.getState().bpm,
      swing: useNotesStore.getState().swing,
      patternSequence: devicePattern?.pattern.sequence,
      patternParts: devicePattern?.patternParts,
      enabledStates: initialEnabledStates,
    };

    // Subscribe to both stores
    const unsubscribeNotes = useNotesStore.subscribe((state) => {
      const currentMusicStore = useMusicStore.getState();
      const currentDevice = currentMusicStore.devices[0]; // Use device 0 (first virtual synth) for config
      const currentDevicePattern = currentDevice?.pattern;

      const currentValues = {
        start: state.start,
        bpm: state.bpm,
        swing: state.swing,
        patternSequence: currentDevicePattern?.pattern.sequence,
        patternParts: currentDevicePattern?.patternParts,
        enabledStates: previousValues.enabledStates, // Don't track enabledStates here, use previous value
      };

      // Check what changed
      const startChanged = currentValues.start !== previousValues.start;
      const bpmChanged = currentValues.bpm !== previousValues.bpm;
      const swingChanged = state.swing !== previousValues.swing;
      const patternChanged =
        currentValues.patternSequence !== previousValues.patternSequence ||
        currentValues.patternParts !== previousValues.patternParts;

      // If only BPM or swing changed and we're playing, just update the tempo without restarting
      if ((bpmChanged || swingChanged) && !startChanged && !patternChanged && currentValues.start && currentDevicePattern) {
        previousValues = { ...currentValues, swing: state.swing };
        const config = {
          bpm: currentValues.bpm,
          swing: state.swing,
          pattern: currentDevicePattern.pattern.sequence,
          patternParts: currentDevicePattern.patternParts,
        };
        currentMusicStore.updateConfigContinuous(config);
        return;
      }

      // If start/stop or pattern changed, do full restart
      if (startChanged || patternChanged) {
        if (isRestartingRef.current) {
          console.log('[APP] ⏭️ Skipping restart - already restarting');
          return;
        }

        console.log('[APP] 🔄 Notes store triggering restart:', {
          startChanged,
          patternChanged,
          start: currentValues.start,
        });

        // Clear any pending flag reset from previous restart
        if (resetTimeoutRef.current !== null) {
          console.log('[APP] 🧹 Clearing pending flag reset timeout');
          clearTimeout(resetTimeoutRef.current);
          resetTimeoutRef.current = null;
        }

        isRestartingRef.current = true;
        previousValues = currentValues;

        // ALWAYS stop first to prevent duplicate NoteGenerators
        currentMusicStore.stopPlayback();

        if (currentValues.start && currentDevicePattern) {
          // Start playback with current device's config
          // Multi-device playback will handle playing all enabled devices independently
          const config = {
            bpm: currentValues.bpm,
            swing: currentValues.swing,
            pattern: currentDevicePattern.pattern.sequence,
            patternParts: currentDevicePattern.patternParts,
          };

          // Wait for startPlayback to complete before resetting flag
          // Add small delay to prevent immediate re-triggers
          currentMusicStore.startPlayback(config, createPlaybackCallbacks()).then(() => {
            resetTimeoutRef.current = window.setTimeout(() => {
              console.log('[APP] ✅ Restart complete (notes store), resetting flag');
              isRestartingRef.current = false;
              resetTimeoutRef.current = null;
            }, 100);
          });
        } else {
          // If not starting playback, reset flag immediately
          isRestartingRef.current = false;
        }
      }
    });

    // Also subscribe to music store for device pattern changes
    const unsubscribeMusic = useMusicStore.subscribe((state) => {
      const currentDevice = state.devices[0]; // Use device 0 (first virtual synth) for config
      const currentDevicePattern = currentDevice?.pattern;
      const notesState = useNotesStore.getState();

      // Track enabled states of all devices
      const enabledStates = state.devices.map((d) => d.enabled).join(",");

      const currentValues = {
        start: notesState.start,
        bpm: notesState.bpm,
        swing: notesState.swing,
        patternSequence: currentDevicePattern?.pattern.sequence,
        patternParts: currentDevicePattern?.patternParts,
        enabledStates,
      };

      // Check what changed
      const enabledStatesChanged = currentValues.enabledStates !== previousValues.enabledStates;
      const patternChanged =
        currentValues.patternSequence !== previousValues.patternSequence ||
        currentValues.patternParts !== previousValues.patternParts;

      // CRITICAL: If we're currently restarting, just update previousValues and return
      // This prevents state changes during startPlayback/stopPlayback from triggering additional restarts
      // But we still need to track the changes so we don't see them as "new" changes later
      if (isRestartingRef.current) {
        console.log('[APP] 🚫 Music store changed during restart - ignoring to prevent double-start');
        // Update previousValues to track changes that happened during restart
        if (enabledStatesChanged || patternChanged) {
          console.log('[APP] 📝 Updating previousValues to track changes during restart');
          previousValues = currentValues;
        }
        return;
      }

      // If enabled states changed, do full restart to add/remove devices from playback
      if (enabledStatesChanged && currentValues.start) {
        if (isRestartingRef.current) {
          console.log('[APP] ⏭️ Skipping restart - already restarting');
          return;
        }

        console.log('[APP] 🔄 Music store triggering restart (enabled states changed):', {
          previousStates: previousValues.enabledStates,
          currentStates: currentValues.enabledStates,
        });

        // Clear any pending flag reset from previous restart
        if (resetTimeoutRef.current !== null) {
          console.log('[APP] 🧹 Clearing pending flag reset timeout');
          clearTimeout(resetTimeoutRef.current);
          resetTimeoutRef.current = null;
        }

        isRestartingRef.current = true;
        previousValues = currentValues;

        // ALWAYS stop first to prevent duplicate NoteGenerators
        state.stopPlayback();

        if (currentDevicePattern) {
          // Then start with fresh config - this will create NoteGenerators for all enabled devices
          const config = {
            bpm: currentValues.bpm,
            swing: currentValues.swing,
            pattern: currentDevicePattern.pattern.sequence,
            patternParts: currentDevicePattern.patternParts,
          };

          // Wait for startPlayback to complete before resetting flag
          // Add small delay to prevent immediate re-triggers
          state.startPlayback(config, createPlaybackCallbacks()).then(() => {
            resetTimeoutRef.current = window.setTimeout(() => {
              console.log('[APP] ✅ Restart complete (enabled states), resetting flag');
              isRestartingRef.current = false;
              resetTimeoutRef.current = null;
            }, 100);
          });
        } else {
          // If not starting, reset flag immediately
          isRestartingRef.current = false;
        }
        return;
      }

      // If pattern content changed (e.g., scrambled), do full restart
      // BUT only if the device is enabled (disabled devices shouldn't affect playback)
      // ALSO: Don't restart if we're currently in a restart (prevents startPlayback state changes from triggering another restart)
      if (patternChanged && !enabledStatesChanged && !isRestartingRef.current) {
        console.log('[APP] 📝 Pattern changed, checking if restart needed...');

        previousValues = currentValues;

        // Only restart playback if the device whose pattern changed is enabled AND we're playing
        if (currentDevice?.enabled && currentValues.start) {
          console.log('[APP] ✅ Pattern changed while playing, restarting playback');

          // Clear any pending flag reset from previous restart
          if (resetTimeoutRef.current !== null) {
            console.log('[APP] 🧹 Clearing pending flag reset timeout');
            clearTimeout(resetTimeoutRef.current);
            resetTimeoutRef.current = null;
          }

          isRestartingRef.current = true;

          // ALWAYS stop first to prevent duplicate NoteGenerators
          state.stopPlayback();

          if (currentDevicePattern) {
            // Then start with fresh config
            const config = {
              bpm: currentValues.bpm,
              swing: currentValues.swing,
              pattern: currentDevicePattern.pattern.sequence,
              patternParts: currentDevicePattern.patternParts,
            };

            // Wait for startPlayback to complete before resetting flag
            // Add small delay to prevent immediate re-triggers
            state.startPlayback(config, createPlaybackCallbacks()).then(() => {
              resetTimeoutRef.current = window.setTimeout(() => {
                console.log('[APP] ✅ Restart complete (pattern changed), resetting flag');
                isRestartingRef.current = false;
                resetTimeoutRef.current = null;
              }, 100);
            });
          } else {
            // If not starting, reset flag immediately
            isRestartingRef.current = false;
          }
        } else {
          console.log('[APP] ⏭️ Pattern changed but not playing or device disabled, skipping restart');
        }
        // If device is disabled or not playing, pattern change doesn't affect playback
        return;
      }

      // If nothing relevant changed, just update previousValues if needed
      if (!patternChanged && !enabledStatesChanged) {
        // No action needed
      }
    });

    return () => {
      unsubscribeNotes();
      unsubscribeMusic();
    };
  }, []);

  return (
    <BrowserRouter>
      <GoogleAnalytics />
      <AppLayout />
    </BrowserRouter>
  );
}

export default function App() {
  return <AppProviders />;
}
